/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { RouteParam, RouteId } from '../../../../router/routes';
import { ListTableColumnConfig } from '../../Common/ListTypes';
import {
    bigValueToHumanStr,
    checkStringValueAndTranslate,
    checkValue,
    DATA_UNAVAILABLE,
    daysToHumanStr,
    filterConfigToOmitKeys,
    formatStringUtcDateTime,
    sortByProp,
    sortVersions,
} from '../../../../utils/utils';
import { WithTranslation } from 'react-i18next';
import {
    DeviceDto,
    DevicesListFilterParams,
    DeviceWorkability,
} from '../../../../data/api/devices/devicesClient.typing';
import { NameValueDataItem } from '../../Common/NameValueGroup';
import { theme } from '../../../../assets/style/them';
import { FilterChipProps } from '../../../FilterPanel/SelectedFiletrsPanel';
import { LocaleId } from '../../../../data/translation/i18n';
import { AppAbility } from '../../../../accessibility/ability';

export const COLOR_BY_WORKABILITY: Record<DeviceWorkability, string> = {
    NEW: theme.palette.amber.main,
    JUNIOR: theme.palette.teal.dark,
    SENIOR: theme.palette.teal.main,
    VETERAN: theme.palette.coral.light,
    INVALID: theme.palette.coral.main,
};

export interface DevicesListTableData {
    factoryNumber: string;
    status: string;
    statusDate: string;
    usbType: string;
    firmwareVersion: string;
    wearout: string;
    age: string;
    experience: string;
    workability: string;

    operatorName?: string;
    operatorTaxNumber?: string;

    isAssotiatedWithCashbox: boolean;
    cashBoxTaxNumber?: string;
    cashBoxStatus?: string;
    temporary?: string;
    cashRegisterFactoryNumber?: string;
    companyTaxNumber?: string;
    storeTaxNumber?: string;
}

export const createTableData = (devices: DeviceDto[], t: WithTranslation['t']): DevicesListTableData[] => {
    return devices.reduce((tableData, device) => {
        const dataItem: Partial<DevicesListTableData> = {};

        dataItem.factoryNumber = checkValue(device.factoryNumber);
        dataItem.status = checkStringValueAndTranslate(device.status, 'device.deviceStatuses.', t).toUpperCase();
        dataItem.statusDate = formatStringUtcDateTime(device.statusDate);
        dataItem.usbType = checkStringValueAndTranslate(device.usbType, 'device.deviceUSBTypes.', t);
        dataItem.firmwareVersion = device.firmwareVersion ? checkValue(device.firmwareVersion) : '-';
        dataItem.wearout = checkValue(bigValueToHumanStr(device.wearout));
        dataItem.age = checkValue(daysToHumanStr(device.age, t));
        dataItem.experience = checkValue(daysToHumanStr(device.experience, t));
        dataItem.workability = checkValue(device.workability);

        dataItem.operatorName = device.operatorName ? checkValue(device.operatorName) : '-';
        dataItem.operatorTaxNumber = checkValue(device.operatorTaxNumber);

        dataItem.isAssotiatedWithCashbox = !!device.cashBoxTaxNumber;
        dataItem.cashBoxTaxNumber = checkValue(device.cashBoxTaxNumber);
        dataItem.cashBoxStatus = checkStringValueAndTranslate(device.cashBoxStatus, 'cashBox.cashboxStatuses.', t);
        dataItem.temporary = device.temp
            ? checkValue(`${t('cashBox.replacementTypes.temporary')}`.toUpperCase())
            : checkValue(`${t('cashBox.replacementTypes.primary')}`.toUpperCase());
        dataItem.cashRegisterFactoryNumber = checkValue(device.cashRegisterFactoryNumber);
        dataItem.companyTaxNumber = checkValue(device.companyTaxNumber);
        dataItem.storeTaxNumber = checkValue(device.storeTaxNumber);

        tableData.push(dataItem);

        return tableData;
    }, []);
};

export const createSelectedFiltersChips = (
    filterParams: DevicesListFilterParams,
    t: WithTranslation['t'],
): FilterChipProps<DevicesListFilterParams>[] => {
    let chips: FilterChipProps<DevicesListFilterParams>[] = [];

    if (filterParams.search_text && filterParams.search_text.length > 0) {
        chips.push({
            keyForValue: 'search_text',
            formattedValue: filterParams.search_text,
            resetValue: '',
        });
    }

    if (filterParams.operatorNames && filterParams.operatorNames.length > 0) {
        const items: FilterChipProps<DevicesListFilterParams>[] = filterParams.operatorNames.map((item, index) => {
            return {
                keyForValue: 'operatorNames',
                index,
                formattedValue: checkValue(item),
            };
        });
        const sortedItems = sortByProp(items, 'formattedValue', LocaleId.En);
        chips = [...chips, ...sortedItems];
    }

    if (filterParams.deviceStatuses && filterParams.deviceStatuses.length > 0) {
        const items: FilterChipProps<DevicesListFilterParams>[] = filterParams.deviceStatuses.map((item, index) => {
            return {
                keyForValue: 'deviceStatuses',
                index,
                formattedValue: checkStringValueAndTranslate(item, 'device.deviceStatuses.', t),
            };
        });
        chips = [...chips, ...items];
    }

    if (filterParams.usbTypes !== null) {
        chips.push({
            keyForValue: 'usbTypes',
            formattedValue: checkStringValueAndTranslate(filterParams.usbTypes, 'device.deviceUSBTypes.', t),
            resetValue: null,
        });
    }

    if (filterParams.workabilities && filterParams.workabilities.length > 0) {
        const items: FilterChipProps<DevicesListFilterParams>[] = filterParams.workabilities.map((item, index) => {
            return {
                keyForValue: 'workabilities',
                index,
                formattedValue: checkStringValueAndTranslate(item, 'device.workabilityLevels.', t),
            };
        });
        chips = [...chips, ...items];
    }

    if (filterParams.firmwareVersions && filterParams.firmwareVersions.length > 0) {
        const items: FilterChipProps<DevicesListFilterParams>[] = filterParams.firmwareVersions.map((item, index) => {
            return {
                keyForValue: 'firmwareVersions',
                index,
                formattedValue: item,
            };
        });
        const sortedItems = sortVersions(items, 'formattedValue', 'desc');
        chips = [...chips, ...sortedItems];
    }

    return chips;
};

const devicesListConfig: ListTableColumnConfig<DevicesListTableData>[] = [
    {
        keyForValue: 'workability',
        translatePath: 'device.workability',
        isWithTooltip: true,
        displayAsColorBar: true,
        cellTestId: 'token-workability-cell',
        align: 'left',
    },
    {
        keyForValue: 'factoryNumber',
        translatePath: 'domain.factoryNumber',
        isSearchable: true,
        isWithTooltip: true,
        link: {
            routeId: RouteId.RegistryDeviceCard,
            routeParamName: RouteParam.DeviceFactoryNumber,
            routeParamKeyForValue: 'factoryNumber',
        },
        isBold: true,
        cellTestId: 'token-factory-number-cell',
        align: 'left',
    },
    {
        keyForValue: 'cashBoxTaxNumber',
        translatePath: 'cashBox.cashboxTaxNumber',
        isSearchable: true,
        isHidden: true, //used for search filter
        align: 'left',
    },
    {
        keyForValue: 'cashRegisterFactoryNumber',
        translatePath: 'cashRegister.cashRegisterFactoryNumber',
        isSearchable: true,
        isHidden: true, //used for search filter
        align: 'left',
    },
    {
        keyForValue: 'storeTaxNumber',
        translatePath: 'store.storeTaxNumber',
        isSearchable: true,
        isHidden: true, //used for search filter
        align: 'left',
    },
    {
        keyForValue: 'companyTaxNumber',
        translatePath: 'company.companyTaxNumber',
        isSearchable: true,
        isHidden: true, //used for search filter
        align: 'left',
    },
    {
        keyForValue: 'status',
        translatePath: 'app.status',
        cellTestId: 'token-status-cell',
        align: 'left',
    },
    {
        keyForValue: 'usbType',
        translatePath: 'device.deviceUSBType',
        cellTestId: 'token-usb-type-cell',
        align: 'left',
    },
    {
        keyForValue: 'firmwareVersion',
        translatePath: 'device.firmware',
        cellTestId: 'token-firmware-cell',
        align: 'center',
    },
    {
        keyForValue: 'wearout',
        translatePath: 'device.wearout',
        cellTestId: 'token-wearout-cell',
        align: 'right',
    },
    {
        keyForValue: 'age',
        translatePath: 'device.age',
        cellTestId: 'token-age-cell',
        align: 'right',
    },
    {
        keyForValue: 'experience',
        translatePath: 'device.experience',
        cellTestId: 'token-experience-cell',
        align: 'right',
    },
    {
        keyForValue: 'operatorTaxNumber',
        translatePath: 'operator.operatorTaxNumber',
        isHidden: true, //not shown, but used for the link below
    },
    {
        keyForValue: 'operatorName',
        translatePath: 'operator.operatorName',
        maxWidth: 300,
        isWithTooltip: true,
        link: {
            routeId: RouteId.RegistryOperatorCard,
            routeParamName: RouteParam.OperatorTaxNumber,
            routeParamKeyForValue: 'operatorTaxNumber',
        },
        isBold: true,
        cellTestId: 'token-operator-name-cell',
        align: 'left',
    },
];

export const getDevicesListConfigByPermits = (ability: AppAbility) => {
    const keysToRemove: (keyof DevicesListTableData)[] = [];

    if (ability.cannot('access', 'RELATED_OPERATOR_DATA')) {
        keysToRemove.push('operatorTaxNumber');
        keysToRemove.push('operatorName');
    }

    if (keysToRemove.length > 0) return filterConfigToOmitKeys<DevicesListTableData>(devicesListConfig, keysToRemove);
    else return devicesListConfig;
};

export const createLeftGroupData = (
    device: DevicesListTableData,
    t: WithTranslation['t'],
    ability: AppAbility,
): NameValueDataItem[] => {
    const workabilityString =
        device.workability !== DATA_UNAVAILABLE
            ? t(`device.workabilityLevels.${device.workability}`).toUpperCase()
            : DATA_UNAVAILABLE;
    const data: NameValueDataItem[] = [
        {
            name: `${t('app.status')}:`,
            value: `${device.status} (${device.statusDate})`,
        },
        {
            name: `${t('device.workability')}:`,
            value: `${workabilityString} (${device.wearout} / ${device.age} / ${device.experience})`,
            valueDescription: `(${t('device.wearout')} / ${t('device.age')} / ${t('device.experience')})`,
        },
    ];

    if (ability.can('access', 'RELATED_OPERATOR_DATA'))
        data.push({
            name: `${t('operator.operatorName')}:`,
            value: device.operatorName,
            valueTooltip: true,
            link:
                device.operatorTaxNumber !== DATA_UNAVAILABLE
                    ? {
                          routeId: RouteId.RegistryOperatorCard,
                          routeParamName: RouteParam.OperatorTaxNumber,
                          routeParamValue: device.operatorTaxNumber,
                      }
                    : null,
        });
    return data;
};

export const createRightGroupData = (device: DevicesListTableData, t: WithTranslation['t']): NameValueDataItem[] => {
    const data: NameValueDataItem[] = [
        {
            name: `${t('device.firmware')}:`,
            value: device.firmwareVersion,
        },
        {
            name: `${t('device.deviceUSBType')}:`,
            value: device.usbType,
        },
    ];
    return data;
};

export const createCashboxGroupData = (device: DevicesListTableData, t: WithTranslation['t']): NameValueDataItem[] => {
    const data: NameValueDataItem[] = [
        {
            name: `${t('cashBox.cashboxTaxNumber')}:`,
            value: device.cashBoxTaxNumber,
            link:
                device.cashBoxTaxNumber !== DATA_UNAVAILABLE
                    ? {
                          routeId: RouteId.RegistryCashboxCard,
                          routeParamName: RouteParam.CashboxTaxNumber,
                          routeParamValue: device.cashBoxTaxNumber,
                      }
                    : null,
        },
        {
            name: `${t('cashBox.replacement')}:`,
            value: device.temporary,
        },
        {
            name: `${t('cashBox.cashboxStatus')}:`,
            value: device.cashBoxStatus,
        },
    ];
    return data;
};

export const createOwnerGroupData = (device: DevicesListTableData, t: WithTranslation['t']): NameValueDataItem[] => {
    const data: NameValueDataItem[] = [
        {
            name: `${t('cashRegister.cashRegisterFactoryNumber')}:`,
            value: device.cashRegisterFactoryNumber,
        },
        {
            name: `${t('company.companyTaxNumber')}:`,
            value: device.companyTaxNumber,
            link:
                device.companyTaxNumber !== DATA_UNAVAILABLE
                    ? {
                          routeId: RouteId.RegistryCompanyCard,
                          routeParamName: RouteParam.CompanyTaxNumber,
                          routeParamValue: device.companyTaxNumber,
                      }
                    : null,
        },
        {
            name: `${t('store.storeTaxNumber')}:`,
            value: device.storeTaxNumber,
            link:
                device.storeTaxNumber !== DATA_UNAVAILABLE
                    ? {
                          routeId: RouteId.RegistryStoreCard,
                          routeParamName: RouteParam.StoreTaxNumber,
                          routeParamValue: `${device.storeTaxNumber}`,
                      }
                    : null,
        },
    ];

    return data;
};
